import i18n from "../../i18n";
import React, { useState, useContext, useEffect } from "react";
import Select from "react-select";
import { Typography } from "../utilities/Typhograpy";
import BoxSkeleton from "../utilities/BoxSkeleton";
import { ChangeWorkspaceApi, UserCompanies } from "../../api/WorkspaceApi";
import { API_BASE_URL } from "../../config/apiConfig";
import swal from "../../services/swalConfig";
import defaultLogo from "../../assets/images/icon/default_company_logo.png";
import GlobalContext from "../../context/GlobalContext";
import { useNavigate } from "react-router-dom";

const API_ORIGIN = API_BASE_URL.replace(/\/v1\/?$/, "");

const normalizeLogoUrl = (logo) => {
  if (!logo) {
    return defaultLogo;
  }

  if (typeof logo === "string" && /^https?:\/\//i.test(logo)) {
    return logo;
  }

  return `${API_ORIGIN}/uploads/logo/company/${logo}`;
};

const toWorkspaceOption = (company) => ({
  value: company.company_id,
  label: company.company_name,
  imageUrl: normalizeLogoUrl(company.company_logo),
});

const toActiveWorkspaceOption = (workspace) => ({
  value: workspace?.company_id,
  label: workspace?.company_name,
  imageUrl: normalizeLogoUrl(workspace?.company_logo),
});

const customStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: "29px",
    border: "1px solid #dcdcdc",
    padding: "3px",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
  }),
  valueContainer: (provided) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "12px",
    cursor: "pointer",
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: "8px",
    backgroundColor: state.isSelected ? "#eee" : "white",
    color: "black",
  }),
};

const customSingleValue = ({ data }) => {
  const handleImageError = (e) => {
    e.target.src = defaultLogo;
  };

  return (
    <div className="select-workspace-container">
      <img
        style={{ width: "25px", marginRight: "8px" }}
        src={data.imageUrl}
        alt={data.label}
        onError={handleImageError}
        className="w-6 h-6 rounded-full mr-2"
      />
      <Typography variant="body2" fontWeight={600}>
        {data.label}
      </Typography>
    </div>
  );
};

const customOption = (props) => {
  const { data, innerRef, innerProps } = props;

  const handleImageError = (e) => {
    e.target.src = defaultLogo;
  };

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="select-workspace-container p-2 hover:bg-gray-100 cursor-pointer gap-1"
    >
      <img
        style={{ width: "25px", marginRight: "8px" }}
        src={data.imageUrl}
        alt={data.label}
        onError={handleImageError}
        className="rounded-full mr-2"
      />
      <Typography variant="body1">{data.label}</Typography>
    </div>
  );
};

const customLoadingOption = (props) => {
  const { innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="w-100 d-flex justify-content-center"
    >
      <BoxSkeleton count="4" size="12" height="25px" />
    </div>
  );
};

const SelectWorkspace = () => {
  const { user, ChangeWorkspace, setLoading } = useContext(GlobalContext);
  const [workspaces, setWorkspaces] = useState([]);
  const [skeletonLoading, setSkeletonLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedWorkspace, setSelectedWorkspace] = useState({
    value: user?.workspace?.company_id,
    label: user?.workspace?.company_name,
    imageUrl: normalizeLogoUrl(user?.workspace?.company_logo),
  });

  useEffect(() => {
    if (user && user.workspace) {
      const nextSelectedWorkspace = toActiveWorkspaceOption(user.workspace);

      setSelectedWorkspace(nextSelectedWorkspace);
      setWorkspaces((prev) => {
        const nextWorkspaces = Array.isArray(prev) ? [...prev] : [];
        const existingIndex = nextWorkspaces.findIndex(
          (item) => item.value === nextSelectedWorkspace.value,
        );

        if (existingIndex >= 0) {
          nextWorkspaces[existingIndex] = nextSelectedWorkspace;
          return nextWorkspaces;
        }

        return nextSelectedWorkspace.value
          ? [nextSelectedWorkspace, ...nextWorkspaces]
          : nextWorkspaces;
      });
    }
  }, [
    user?.workspace?.company_id,
    user?.workspace?.company_name,
    user?.workspace?.company_logo,
  ]);

  useEffect(() => {
    loadWorkspace();
  }, [user?.user_id]);

  const loadWorkspace = async () => {
    try {
      setSkeletonLoading(true);

      const response = await UserCompanies();

      console.log(response.data);

      if (response.status != 200) {
        console.error("Failed to load workspaces:", response);
        setSkeletonLoading(false);
        return;
      }

      const dataCompanies = Array.isArray(response.data) ? response.data : [];
      const companies = dataCompanies.map(toWorkspaceOption);
      const activeWorkspaceOption = user?.workspace
        ? toActiveWorkspaceOption(user.workspace)
        : null;

      const mergedCompanies = activeWorkspaceOption?.value
        ? companies.map((company) =>
            company.value === activeWorkspaceOption.value
              ? activeWorkspaceOption
              : company,
          )
        : companies;

      setWorkspaces(mergedCompanies);
      setSkeletonLoading(false);

      // Update selectedWorkspace if current workspace is not in the list
      const currentWorkspace = mergedCompanies.find(
        (c) => c.value === selectedWorkspace.value,
      );
      if (!currentWorkspace && mergedCompanies.length > 0) {
        // If current workspace not found, select the first one
        setSelectedWorkspace(mergedCompanies[0]);
      } else if (currentWorkspace) {
        setSelectedWorkspace(currentWorkspace);
      }
    } catch (error) {
      console.error("Error loading workspaces:", error);
      setSkeletonLoading(false);
    }
  };

  const handleMenuOpen = () => {
    loadWorkspace();
  };

  const handleChangeWorkspace = async (selectedOption) => {
    setLoading(true);

    try {
      const date = new Date().toISOString().slice(0, 10);
      const time = new Date()
        .toLocaleTimeString(undefined, { hour12: false })
        .replace(/\./g, ":");

      const result = await ChangeWorkspaceApi({
        company_id: selectedOption.value,
        date: date,
        time: time,
      });

      if (result.status == 200) {
        setSelectedWorkspace(selectedOption);
        ChangeWorkspace({
          ...result.data,
          company_logo: selectedOption.imageUrl,
        });
        navigate("/dashboard");
      }
    } catch (error) {
      swal.fire({
        title: i18n.t("errors.oops"),
        text: i18n.t("errors.generic"),
        icon: "error",
        showCancelButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Select
      options={workspaces}
      getOptionLabel={(option) => option.label || ""}
      getOptionValue={(option) => option.value}
      hideSelectedOptions={false}
      isOptionSelected={(option, selectValue) =>
        selectValue.some((selected) => selected.value === option.value)
      }
      components={{
        SingleValue: customSingleValue,
        Option: skeletonLoading ? customLoadingOption : customOption,
      }}
      value={selectedWorkspace}
      onChange={handleChangeWorkspace}
      isLoading={skeletonLoading}
      isSearchable={false}
      className="w-64"
      classNamePrefix="workspace-select"
      styles={customStyles}
      onMenuOpen={handleMenuOpen}
    />
  );
};

export default SelectWorkspace;
